import { useEffect, useRef } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

/* ═══════════════════════════════════════════════════════════
   Carte de la page hôtel

   - Coordonnées renseignées : notre carte, avec le point exact.
   - Pas de coordonnées : la carte Google par ville, comme avant.

   Les liens Itinéraire / Google Maps utilisent les mêmes
   coordonnées : les deux cartes pointent au même endroit.
   ═══════════════════════════════════════════════════════════ */

maplibregl.setWorkerUrl("/maplibre-gl-worker.js");

const STYLE_PLAN = "https://tiles.openfreemap.org/styles/liberty";

// Secours si le plan détaillé ne s'affiche pas
const STYLE_FALLBACK = {
  version: 8,
  sources: {
    plan: {
      type: "raster",
      tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
      maxzoom: 18,
      attribution: "© Esri, © OpenStreetMap",
    },
  },
  layers: [{ id: "plan", type: "raster", source: "plan" }],
};

const G = "var(--k-green)";

export const hasCoords = (hotel) =>
  hotel?.location?.lat != null && hotel?.location?.lng != null;

// Liens Google Maps : coordonnées exactes en priorité, sinon la ville
export function googleLinks(hotel) {
  if (hasCoords(hotel)) {
    const q = `${hotel.location.lat},${hotel.location.lng}`;
    return {
      directions: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
      open: `https://www.google.com/maps/search/?api=1&query=${q}`,
    };
  }
  const q = encodeURIComponent(`${hotel?.city || ""}, Comores`);
  return {
    directions: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
    open: `https://www.google.com/maps/search/?api=1&query=${q}`,
  };
}

function pinElement(width) {
  const el = document.createElement("div");
  el.style.filter = "drop-shadow(0 2px 3px rgba(0,0,0,0.3))";
  el.innerHTML = `
    <svg width="${width}" height="${Math.round(width * 1.3)}" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 0C7.6 0 0 7.6 0 17c0 12.4 17 27 17 27s17-14.6 17-27C34 7.6 26.4 0 17 0z" fill="#174D3A"/>
      <circle cx="17" cy="17" r="6.5" fill="#FFB32E"/>
    </svg>`;
  return el;
}

function attachFallback(map) {
  let rendered = false;
  let switched = false;
  const toFallback = () => {
    if (switched || rendered) return;
    switched = true;
    map.setStyle(STYLE_FALLBACK);
  };
  const onIdle = () => { rendered = true; clearTimeout(timer); };
  const onError = () => { if (!map.isStyleLoaded()) toFallback(); };
  const timer = setTimeout(toFallback, 10000);
  map.once("idle", onIdle);
  map.on("error", onError);
  return () => { clearTimeout(timer); map.off("idle", onIdle); map.off("error", onError); };
}

function ExactMap({ lat, lng, height, pinWidth }) {
  const el = useRef(null);

  useEffect(() => {
    const map = new maplibregl.Map({
      container: el.current,
      style: STYLE_PLAN,
      center: [lng, lat],
      zoom: 15,
      attributionControl: { compact: true },
      // La page reste scrollable : on ne zoome pas à la molette par accident
      scrollZoom: false,
      dragRotate: false,
      pitchWithRotate: false,
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    // Crédits repliés en petit "i" : dépliés, ils cachent la moitié de la carte
    map.once("load", () => {
      el.current?.querySelector(".maplibregl-ctrl-attrib")?.classList.remove("maplibregl-compact-show");
    });

    new maplibregl.Marker({ element: pinElement(pinWidth), anchor: "bottom" })
      .setLngLat([lng, lat])
      .addTo(map);

    const stopFallback = attachFallback(map);
    return () => { stopFallback(); map.remove(); };
  }, [lat, lng, pinWidth]);

  return <div ref={el} style={{ width: "100%", height, display: "block" }} />;
}

/**
 * Carte + adresse + liens.
 * @param {object} hotel      l'hôtel (city, island, location)
 * @param {number} height     hauteur de la carte en px
 * @param {boolean} compact   version plus petite (grille 3 colonnes)
 */
export default function HotelMap({ hotel, height = 160, compact = false }) {
  const exact = hasCoords(hotel);
  const links = googleLinks(hotel);
  const place = compact
    ? `${hotel.city}, Comores`
    : `${hotel.city}${hotel.island ? `, ${hotel.island}` : ""}, Comores`;

  return (
    <>
      {exact ? (
        <ExactMap lat={hotel.location.lat} lng={hotel.location.lng} height={height} pinWidth={compact ? 20 : 24} />
      ) : (
        <iframe
          title="map"
          width="100%"
          height={height}
          frameBorder="0"
          style={{ display: "block", border: 0 }}
          src={`https://maps.google.com/maps?q=${encodeURIComponent((hotel.city || "") + ", Comores")}&z=13&output=embed`}
          loading="lazy"
          allowFullScreen
        />
      )}

      <div style={{ padding: compact ? "10px 14px" : "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: compact ? 4 : 6, marginBottom: compact ? 6 : 8 }}>
          <svg width={compact ? 11 : 13} height={compact ? 11 : 13} viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M12 22s7-6.1 7-12a7 7 0 10-14 0c0 5.9 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" />
          </svg>
          <span style={{ fontSize: compact ? 12 : 13, color: "#374151", fontWeight: 500 }}>{place}</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <a href={links.directions} target="_blank" rel="noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: G, color: "#fff", textDecoration: "none",
              fontSize: 12, fontWeight: 700,
              padding: compact ? "6px 12px" : "7px 14px", borderRadius: 8,
            }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 11l18-8-8 18-2-8-8-2z" />
            </svg>
            Itinéraire
          </a>
          <a href={links.open} target="_blank" rel="noreferrer"
            style={{ fontSize: 12, color: G, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>
            Ouvrir dans Google Maps →
          </a>
        </div>
      </div>
    </>
  );
}